import { Switch } from '../../components/ui/switch';
import SubjectTabs from '../generic/SubjectTabs';
import { Info } from '@phosphor-icons/react';
import React, { useState, useEffect } from 'react';
import StatCell from '../generic/StatCell';
import { QSTATUS, QUESTION_TYPES } from '../../../../../apps/frontend/src/utils/strings';
import TableLoader from '../generic/TableLoader';
import NoData from '../generic/NoData';
import moment from 'moment';
import { CheckSquareIcon, WarningOctagonIcon, XSquareIcon, Checks, PercentIcon } from '@phosphor-icons/react';
import TableHeader from '../../components/generic/TableHeader';
import { useNavigate } from 'react-router-dom';

const QsTypeBreakupTestwise = ({ data, loading, onResetFilters }) => {
  const [activeSubject, setActiveSubject] = useState('overall');
  const [showPercentage, setShowPercentage] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [questionRows, setQuestionRows] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (data?.timeline?.length > 0) {
      const typesMap = new Map();

      data.timeline.forEach(({ basicAnalysis }) => {
        basicAnalysis?.subjectWise?.forEach((subject) => {
          Object.keys(subject).forEach((key) => {
            const item = subject[key];
            if (item && typeof item === 'object' && item.questionTypeMeta && typeof item.totalQuestions === 'number') {
              const typeId = key;
              if (!typesMap.has(typeId)) {
                typesMap.set(typeId, {
                  key: typeId,
                  title: item.questionTypeMeta.questionTypeTitle,
                  position: item.questionTypeMeta.position || 999,
                  lightIcon: item.questionTypeMeta.lightThemeIcon,
                  darkIcon: item.questionTypeMeta.darkThemeIcon,
                });
              }
            }
          });
        });
      });

      const sortedTypes = Array.from(typesMap.values()).sort((a, b) => a.position - b.position);
      setQuestionTypes(sortedTypes);
    }
  }, [data]);

  useEffect(() => {
    if (data?.timeline?.length > 0) {
      // Collect all unique subjects across all test data
      const allSubjects = new Map();

      data.timeline.forEach(({ basicAnalysis }) => {
        if (basicAnalysis?.subjectWise) {
          basicAnalysis.subjectWise.forEach((subject) => {
            if (!allSubjects.has(subject.subjectMeta._id)) {
              allSubjects.set(subject.subjectMeta._id, {
                _id: subject.subjectMeta._id,
                title: subject.subjectMeta.title,
                icon: subject.subjectMeta.icon || Checks,
                darkIcon: subject.subjectMeta.darkIcon,
                color: subject.subjectMeta.color,
              });
            }
          });
        }
      });

      const subjects = Array.from(allSubjects.values());
      setHeaders([...subjects.filter((sub) => sub._id !== 'overall')]);
    }
  }, [data]);

  useEffect(() => {
    if (data?.timeline?.[0]?.basicAnalysis?.subjectWise) {
      const rows = data.timeline[0].basicAnalysis.subjectWise.map((subject) => ({
        _id: subject.subjectMeta._id,
        title: subject.subjectMeta.title,
        icon: subject.subjectMeta.icon,
        color: subject.subjectMeta.color,
        singleCorrect: {
          ...subject.singleCorrect,
          icon: subject?.singleCorrect?.questionTypeMeta?.lightThemeIcon || '.',
          title: subject?.singleCorrect?.questionTypeMeta?.questionTypeTitle || 'Single Correct',
        },
        numerical: {
          ...subject.numerical,
          icon: subject?.numerical?.questionTypeMeta?.lightThemeIcon || '.',
          title: subject?.numerical?.questionTypeMeta?.questionTypeTitle || 'Numerical Type',
        },
      }));
      setQuestionRows(rows);
    }
  }, [data]);

  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  const getQuestionTypeData = (subjectData, typeKey) => {
    return (
      subjectData?.[typeKey] || {
        correct: 0,
        incorrect: 0,
        unanswered: 0,
        notvisited: 0,
        totalQuestions: 0,
      }
    );
  };

  return (
    <>
      <div className="border-l border-r border-b border-[var(--q3-stroke-light)] rounded-b-lg overflow-x-auto">
        <TableHeader
          headerTitle="Test-wise Breakdown"
          showBottomBorder={false}
          headerSwitch={
            <span className="flex items-center gap-1">
              <PercentIcon size={16} weight="bold" />
              Show Percentage
            </span>
          }
          headerSwitchCase={showPercentage}
          headerSwitchHandler={setShowPercentage}
        />

        {/* Legend */}
        <div className="mb-4 text-sm flex flex-row flex-wrap items-start sm:items-center gap-4 sm:gap-6 ml-4">
          <div className="flex items-center gap-2">
            <CheckSquareIcon size={18} color="var(--q3-green-normal)" />
            <span className="text-[var(--q3-green-normal)]">Correct Attempts</span>
          </div>
          <div className="flex items-center gap-2">
            <XSquareIcon size={18} color="var(--q3-danger-normal)" />
            <span className="text-[var(--q3-danger-normal)]">Incorrect Attempts</span>
          </div>
          <div className="flex items-center gap-2">
            <WarningOctagonIcon size={18} color="var(--q3-neutral-light)" />
            <span className="text-[var(--q3-neutral-light)]">Not Attempted</span>
          </div>
        </div>

        {/* Subject Tabs with scroll */}
        <div className="overflow-x-auto w-full">
          <div className="min-w-0">
            <SubjectTabs
              subjects={headers}
              activeSubject={activeSubject}
              onSubjectChange={setActiveSubject}
              showOverallTab={true}
            />
          </div>
        </div>

        {/* Scrollable Table */}
        <div className="overflow-x-auto">
          <div style={{ minWidth: `${220 + questionTypes.length * 3 * 100}px` }}>
            <div className="rounded-b-lg overflow-x-auto">
              <table className="w-full border-collapse table-fixed">
                <thead>
                  <tr className="bg-[var(--q3-surface-dim)] text-sm font-medium text-[var(--q3-neutral-default)]">
                    <td className="border-r border-[var(--q3-stroke-light)] w-[220px]">
                      <div className="p-4 flex items-center justify-start">Test Title</div>
                    </td>

                    {questionTypes.map((qType, idx) => (
                      <td key={qType.key} className="border-r border-[var(--q3-stroke-light)] text-center" colSpan="3">
                        <div className="p-4 flex items-center justify-center">{qType.title}</div>
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <TableLoader loading={loading}>
                    {data?.timeline?.length === 0 ? (
                      <tr>
                        <td colSpan={1 + questionTypes.length * 3}>
                          <NoData onResetAll={onResetFilters} />
                        </td>
                      </tr>
                    ) : (
                      data?.timeline?.map(({ basicAnalysis, testId, packId }, index) => {
                        const subjectData =
                          activeSubject === 'overall'
                            ? basicAnalysis.subjectWise.find((subject) => subject.subjectMeta._id === 'overall')
                            : basicAnalysis.subjectWise.find((subject) => subject.subjectMeta._id === activeSubject);

                        if (!subjectData) return null;

                        return (
                          <tr
                            key={index}
                            className="border-t border-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]"
                          >
                            {/* Test Title (Clickable) */}
                            <td
                              className="border-r border-[var(--q3-stroke-light)] min-w-[275px] cursor-pointer hover:bg-[var(--q3-surface-hover)]"
                              onClick={() => navigate(`/${packId}/${testId}/analysis`)}
                            >
                              <div className="p-4">
                                <div className="font-medium truncate whitespace-nowrap overflow-hidden max-w-[220px]">
                                  {basicAnalysis.testTitle}
                                </div>
                                <div className="text-xs text-[var(--q3-neutral-light)]">
                                  {moment(basicAnalysis.submittedAt).format('D MMM YYYY')}
                                  {basicAnalysis.isBonusAvailable &&
                                    basicAnalysis.isBonusAttempted &&
                                    ' | Bonus Qs Attempted'}
                                </div>
                              </div>
                            </td>

                            {questionTypes.map((qType) => {
                              const typeData = getQuestionTypeData(subjectData, qType.key);
                              return (
                                <React.Fragment key={qType.key}>
                                  {/* Correct */}
                                  <td className="border-r border-[var(--q3-stroke-light)]">
                                    <div className="p-4 flex items-center gap-2 justify-center">
                                      <CheckSquareIcon size={18} color="var(--q3-green-normal)" />
                                      <span>
                                        {showPercentage
                                          ? `${calculatePercentage(typeData.correct, typeData.totalQuestions)}%`
                                          : `${typeData.correct}/${typeData.totalQuestions}`}
                                      </span>
                                    </div>
                                  </td>

                                  {/* Incorrect */}
                                  <td className="border-r border-[var(--q3-stroke-light)]">
                                    <div className="p-4 flex items-center gap-2 justify-center">
                                      <XSquareIcon size={18} color="var(--q3-danger-normal)" />
                                      <span>
                                        {showPercentage
                                          ? `${calculatePercentage(typeData.incorrect, typeData.totalQuestions)}%`
                                          : `${typeData.incorrect}/${typeData.totalQuestions}`}
                                      </span>
                                    </div>
                                  </td>

                                  {/* Unanswered */}
                                  <td
                                    className={
                                      qType.key === questionTypes[questionTypes.length - 1]?.key
                                        ? ''
                                        : 'border-r border-[var(--q3-stroke-light)]'
                                    }
                                  >
                                    <div className="p-4 flex items-center gap-2 justify-center">
                                      <WarningOctagonIcon size={18} color="var(--q3-neutral-light)" />
                                      <span>
                                        {showPercentage
                                          ? `${calculatePercentage(typeData.unanswered, typeData.totalQuestions)}%`
                                          : `${typeData.unanswered}/${typeData.totalQuestions}`}
                                      </span>
                                    </div>
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        );
                      })
                    )}
                  </TableLoader>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QsTypeBreakupTestwise;
